import React from 'react';
import { Link } from 'react-router-dom';
import { IconsLAccent01, IconsLRed } from '../Icons/Icons_L';
import { ArrowL, Error as ErrorIcon, PDF } from '../Icons/CustomIcons';
import FieldInfo from './FieldInfo';
import { Label } from './Label';
import { Input } from './Input';

export const Dialog = ({ open, onOpenChange, children }) => {
  const trigger = React.Children.toArray(children).find(
    child => child.type === DialogTrigger
  );
  const content = React.Children.toArray(children).find(
    child => child.type === DialogContent
  );

  return (
    <>
      {trigger && React.cloneElement(trigger, { onOpenChange })}
      {open && (
        <div className="dialog-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            onOpenChange(false);
          }
        }}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            {content}
          </div>
        </div>
      )}
    </>
  );
};

export const DialogTrigger = ({ asChild, children, onOpenChange, ...props }) => {
  const handleClick = () => {
    if (onOpenChange) {
      onOpenChange(true);
    }
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...props,
      onClick: handleClick
    });
  }

  return <button {...props} onClick={handleClick}>{children}</button>;
};

export const DialogContent = ({ children, className = '' }) => {
  return <div className={`dialog-inner ${className}`}>{children}</div>;
};

export const DialogHeader = ({ children, className = '' }) => {
  return <div className={`dialog-header ${className}`}>{children}</div>;
};

export const DialogTitle = ({ children, className = '' }) => {
  return <h2 className={`dialog-title ${className}`}>{children}</h2>;
};

export const DialogDescription = ({ children, className = '' }) => {
  return <p className={`dialog-description ${className}`}>{children}</p>;
};

export const ValidFromTo = ({
  validFrom,
  validUntil,
  onChangeFrom,
  onChangeUntil,
  fromRequired = false,
  labelFrom,
  labelUntil,
  placeholderFrom,
  placeholderUntil,
}) => {
  console.log('[ValidFromTo] fromRequired:', fromRequired);
  return (
    <div className="validity-dates">
      <div className="validity-field">
        <label className="body Font-M-I-130-T Colors-Black validity-label">
          {labelFrom ?? 'Gültig ab'}
          {fromRequired ? <span className="Colors-Red"> *</span> : ' (optional)'}
        </label>
        <Input
          type="date"
          value={validFrom || ''}
          onChange={onChangeFrom}
          required={fromRequired}
          placeholder={placeholderFrom}
        />
      </div>
      <div className="validity-field">
        <label className="body Font-M-I-130-T Colors-Black validity-label">{labelUntil ?? 'Gültig bis (optional)'}</label>
        <Input
          type="date"
          value={validUntil || ''}
          onChange={onChangeUntil}
          placeholder={placeholderUntil}
        />
      </div>
    </div>
  );
};

export const FormLabelWInfo = ({ htmlFor, text, children, error = false }) => (
  <div className="form-label-with-info">
    <Label
      htmlFor={htmlFor}
      className="body Font-M-I-130-T"
      style={error ? { color: 'var(--Colors-Red)', display: 'inline-flex', alignItems: 'center', gap: '10px' } : undefined}
    >
      {error && <ErrorIcon size={16} />}
      {children}
    </Label>
    <FieldInfo text={text} />
  </div>
);

export const FormLabel = ({ htmlFor, children, error = false }) => (
  <div className="form-label-with-info">
    <Label htmlFor={htmlFor} className="body Font-M-I-130-T" style={error ? { color: 'var(--Colors-Red)' } : undefined}>{children}</Label>
  </div>
);

export const ValidationError = ({ children, className = '' }) => (
  <div className={`address-validation address-validation--invalid${className ? ' ' + className : ''}`}>
    <ErrorIcon size={16} />
    {children}
  </div>
);

export const FormSelector = ({ value, onChange, children, className = '', ...props }) => (
  <select
    value={value}
    onChange={onChange}
    className={`input text__input${className ? ` ${className}` : ''}`}
    {...props}
  >
    {children}
  </select>
);

export const IconHeader = ({ Icon, info, danger = false, children }) => (
  <h3 className="icon-header">
    <span className={`icon-wrap${danger ? ' is-danger' : ''}`}>{Icon}</span>
    {children}
    {info && <FieldInfo text={info} />}
  </h3>
);

export const AuthDesktopHeader = ({ title, subtitle }) => (
  <div className="auth-desktop-header">
    <Link to="/" aria-label="Zur Startseite">
      <img
        src={`${process.env.PUBLIC_URL}/favicon.svg`}
        className="auth-desktop-logo"
        alt="grosskinder-aktiv.ch"
      />
    </Link>
    <h1 className="auth-desktop-title">{title}</h1>
    {subtitle && <p className="auth-desktop-subtitle">{subtitle}</p>}
  </div>
);

export const IconContainer = ({ IconsL = IconsLAccent01, Icon }) => (
  <div style={{ display: 'flex', marginLeft: 'var(--margins, 20px)'}}>
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <IconsL size={84} />
      {Icon && <Icon size={28} color="#fff" style={{ position: 'absolute' }} />}
    </div>
  </div>
);

export function DoCancelDialog({ icon: Icon, title, text, message, onConfirm, onCancel, danger = false }) {
  const IconsL = danger ? IconsLRed : IconsLAccent01;
  return (
    <div className="map-logout-overlay" role="dialog" aria-modal="true" aria-labelledby="do-cancel-dialog-title">
      <div className="map-logout-dialog">
        <div className="map-logout-header">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconsL size={84} />
              {Icon && <Icon size={28} color="#fff" style={{ position: 'absolute' }} />}
            </div>
          </div>
          <h2 id="do-cancel-dialog-title" className="map-logout-title">{title}</h2>
          <p className="map-logout-message">{message}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--Spacings-Spacing-XS)' }}>
          <button type="button" className="btn btn-primary map-logout-confirm-btn" onClick={onConfirm}>
            {Icon && <Icon size={20} style={{ marginRight: '0.4rem' }} />}
            {text}
          </button>
          <button type="button" className="btn btn-secondary map-logout-cancel-btn" onClick={onCancel}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}

export const ValidityPeriodText = ({ validFrom, validUntil, freely_accessible }) => {
  if (!validFrom && !validUntil) return null;
  const prefix = freely_accessible ? 'Aktivität ist frei zugänglich' : 'Die Termine gelten';
  return (
    <p className="card-subtitle">
      {validFrom && validUntil && `${prefix} von ${new Date(validFrom).toLocaleDateString('de-DE')} – ${new Date(validUntil).toLocaleDateString('de-DE')}.`}
      {validFrom && !validUntil && `${prefix} ab ${new Date(validFrom).toLocaleDateString('de-DE')}.`}
      {!validFrom && validUntil && `${prefix} bis ${new Date(validUntil).toLocaleDateString('de-DE')}.`}
    </p>
  );
};

export function GoToDialog({ icon: Icon, title, text, message, onConfirm}) {
  return (    
    <div className="map-logout-overlay" role="dialog" aria-modal="true" aria-label="Passwort gesetzt">
      <div className="map-logout-dialog">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconsLAccent01 size={84} />
            {Icon && <Icon size={28} color="#fff" style={{ position: 'absolute' }} />}
          </div>
        </div>
        <h2 className="map-logout-title">{title}</h2>
        <p className="map-logout-message">{message}</p>
        <button type="button" className="map-logout-confirm-btn" onClick={onConfirm}>
          <ArrowL size={18} aria-hidden="true" style={{ marginRight: '0.5rem' }} />
          {text}
        </button>
      </div>
    </div>
  );
}

export function PdfLoadingDialog({ state, onDownload, onClose }) {
  return (
    <div className="map-logout-overlay" role="dialog" aria-modal="true" aria-label="PDF">
      <div className="map-logout-dialog" style={{ alignItems: 'center', textAlign: 'center' }}>
        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconsLAccent01 size={84} />
          <PDF size={28} color="#fff" style={{ position: 'absolute' }} />
        </div>

        {state === 'loading' && (
          <>
            <h2 className="map-logout-title">PDF wird erstellt…</h2>
            <p className="map-logout-message">Bitte einen Moment warten.</p>
            <div style={{
              width: 36,
              height: 36,
              border: '4px solid var(--Colors-Grey-03, #DCE1E4)',
              borderTopColor: 'var(--Colors-Accent-01, #006475)',
              borderRadius: '50%',
              animation: 'pdf-spin 0.8s linear infinite',
              margin: '0.75rem auto 0',
            }} />
            <style>{`@keyframes pdf-spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {state === 'ready' && (
          <>
            <h2 className="map-logout-title">PDF ist bereit</h2>
            <p className="map-logout-message">Das PDF wurde erfolgreich erstellt.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--Spacings-Spacing-XS)', marginTop: '0.75rem' }}>
              <button type="button" className="btn btn-primary map-logout-confirm-btn" onClick={onDownload}>
                <PDF size={20} style={{ marginRight: '0.4rem' }} />
                PDF herunterladen
              </button>
              <button type="button" className="btn btn-secondary map-logout-cancel-btn" onClick={onClose}>
                Schließen
              </button>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <h2 className="map-logout-title">Fehler beim Erstellen</h2>
            <p className="map-logout-message">Das PDF konnte nicht erstellt werden. Bitte versuchen Sie es erneut.</p>
            <button type="button" className="btn btn-secondary map-logout-cancel-btn" onClick={onClose} style={{ marginTop: '0.75rem' }}>
              Schließen
            </button>
          </>
        )}
      </div>
    </div>
  );
}
